import React, { useRef, useState, useCallback, useEffect } from 'react';

/**
 * UIComponentWrapper - Wrapper for placed UI components
 * Handles selection, dragging, and resizing with local state during interactions
 * Constrains components to stay within artboard bounds
 * Supports snap-to-edge alignment with sibling components
 */
const UIComponentWrapper = ({
    component,
    isSelected,
    onSelect,
    onMove,
    onResize,
    artboardWidth = 375,
    artboardHeight = 812,
    siblingComponents = [],
    selectedComponentIds = new Set(),
    groupDragOffset = null,
    onGroupMove,
    onGroupDrag,
    onGroupDragEnd,
    onDrag,
    onDragEnd,
    children
}) => {
    const wrapperRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);

    // Track pending updates to prevent snap-back before API responds
    const pendingPosition = useRef(null);
    const pendingDimensions = useRef(null);

    // Track previous group drag offset to absorb into localPosition when drag ends
    const prevGroupOffset = useRef(null);

    // Local position/dimensions for smooth dragging (updated in real-time)
    // Clamp initial dimensions to fit within artboard bounds
    const [localPosition, setLocalPosition] = useState(() => ({
        x: component.position_x || 0,
        y: component.position_y || 0
    }));
    const [localDimensions, setLocalDimensions] = useState(() => {
        const pos = { x: component.position_x || 0, y: component.position_y || 0 };
        const rawWidth = component.width || 100;
        const rawHeight = component.height || 40;
        return {
            width: Math.max(40, Math.min(rawWidth, artboardWidth - pos.x)),
            height: Math.max(20, Math.min(rawHeight, artboardHeight - pos.y))
        };
    });

    // Helper to clamp position within artboard bounds
    const clampToBounds = useCallback((pos, dims) => {
        const width = dims?.width || localDimensions.width;
        const height = dims?.height || localDimensions.height;

        return {
            x: Math.max(0, Math.min(pos.x, artboardWidth - width)),
            y: Math.max(0, Math.min(pos.y, artboardHeight - height))
        };
    }, [artboardWidth, artboardHeight, localDimensions]);

    // Sync local state with component props when they change (from backend updates)
    // Only sync if we're not dragging/resizing AND no pending update OR props match pending
    useEffect(() => {
        if (isDragging || isResizing) return;

        const rawPos = { x: component.position_x || 0, y: component.position_y || 0 };
        const rawDims = { width: component.width || 100, height: component.height || 40 };

        // Clamp dimensions to fit within artboard bounds
        const clampedDims = {
            width: Math.min(rawDims.width, artboardWidth - rawPos.x),
            height: Math.min(rawDims.height, artboardHeight - rawPos.y)
        };

        // Ensure minimum size
        clampedDims.width = Math.max(40, clampedDims.width);
        clampedDims.height = Math.max(20, clampedDims.height);

        // Check if props match pending (meaning API responded with our update)
        if (pendingPosition.current) {
            if (Math.abs(rawPos.x - pendingPosition.current.x) < 1 &&
                Math.abs(rawPos.y - pendingPosition.current.y) < 1) {
                // Props match pending - clear pending and allow sync
                pendingPosition.current = null;
            } else {
                // Props don't match pending yet - API still in flight, don't reset
                return;
            }
        }

        if (pendingDimensions.current) {
            if (Math.abs(rawDims.width - pendingDimensions.current.width) < 1 &&
                Math.abs(rawDims.height - pendingDimensions.current.height) < 1) {
                pendingDimensions.current = null;
            } else {
                return;
            }
        }

        setLocalPosition(rawPos);
        setLocalDimensions(clampedDims);
    }, [component.position_x, component.position_y, component.width, component.height, isDragging, isResizing, artboardWidth, artboardHeight]);

    // Absorb group drag offset into localPosition when drag ends (greedy update)
    useEffect(() => {
        // If we previously had an offset for this component (not the one being dragged), absorb it
        const prev = prevGroupOffset.current;
        if (prev &&
            !groupDragOffset &&
            prev.draggingId !== component.id &&
            isSelected) {
            const deltaX = prev.deltaX;
            const deltaY = prev.deltaY;
            // Update local position to include the offset (greedy - before API responds)
            setLocalPosition(currentPos => {
                const newPos = {
                    x: currentPos.x + deltaX,
                    y: currentPos.y + deltaY
                };
                // Mark as pending so we don't snap back when API updates
                pendingPosition.current = newPos;
                return newPos;
            });
        }
        // Track the current offset for next comparison
        prevGroupOffset.current = groupDragOffset;
    }, [groupDragOffset, component.id, isSelected]);

    // Handle mouse down for dragging
    const handleMouseDown = useCallback((e) => {
        // Don't start drag if clicking resize handle
        if (e.target.classList.contains('resize-handle')) return;

        e.stopPropagation();
        e.preventDefault();

        onSelect(e);
        setIsDragging(true);

        const startX = e.clientX;
        const startY = e.clientY;
        const startPosX = localPosition.x;
        const startPosY = localPosition.y;

        // Get zoom scale from TransformComponent for accurate drag
        let scale = 1;
        const transformComponent = wrapperRef.current?.closest('.react-transform-component');
        if (transformComponent) {
            const style = window.getComputedStyle(transformComponent);
            const transform = style.transform;
            if (transform && transform !== 'none') {
                const match = transform.match(/matrix\(([^,]+)/);
                if (match) {
                    scale = parseFloat(match[1]) || 1;
                }
            }
        }

        // Track current position during drag (including snap)
        let currentPos = { x: startPosX, y: startPosY };

        // Capture dimensions at drag start to avoid closure issues
        const dragDimensions = { ...localDimensions };

        const handleMouseMove = (moveEvent) => {
            moveEvent.preventDefault();
            // Divide delta by scale to compensate for zoom
            const deltaX = (moveEvent.clientX - startX) / scale;
            const deltaY = (moveEvent.clientY - startY) / scale;

            // Clamp position to artboard bounds first (pass explicit dimensions)
            let newPos = clampToBounds({
                x: startPosX + deltaX,
                y: startPosY + deltaY
            }, dragDimensions);

            // Apply snap-to-edge if onDrag callback is provided
            if (onDrag) {
                newPos = onDrag(component.id, newPos, dragDimensions);
                // Re-clamp after snap to ensure we stay within bounds
                newPos = clampToBounds(newPos, dragDimensions);
            }

            currentPos = newPos;
            setLocalPosition(newPos);

            // Update group drag offset for other selected components
            if (selectedComponentIds.size > 1 && selectedComponentIds.has(component.id) && onGroupDrag) {
                const delta = { x: newPos.x - startPosX, y: newPos.y - startPosY };
                onGroupDrag(component.id, delta);
            }
        };

        const handleMouseUp = () => {
            // Use the current snapped position (not recalculated from delta)
            const finalPosition = {
                x: Math.round(currentPos.x),
                y: Math.round(currentPos.y)
            };

            // Keep visual in sync
            setLocalPosition(finalPosition);
            setIsDragging(false);

            // Clear alignment guides
            if (onDragEnd) {
                onDragEnd();
            }

            // Clear group drag offset
            if (onGroupDragEnd) {
                onGroupDragEnd();
            }

            // Only persist if position actually changed
            if (finalPosition.x !== startPosX || finalPosition.y !== startPosY) {
                // Mark as pending before API call
                pendingPosition.current = finalPosition;
                onMove(finalPosition);

                // If part of multi-selection, move other selected components by the same delta
                const selectedCount = selectedComponentIds?.size || 0;
                const isInSelection = selectedComponentIds?.has?.(component.id);

                if (selectedCount > 1 && isInSelection && onGroupMove) {
                    const delta = {
                        x: finalPosition.x - startPosX,
                        y: finalPosition.y - startPosY
                    };
                    onGroupMove(component.id, delta);
                }
            }

            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [localPosition, localDimensions, onMove, onSelect, clampToBounds, onDrag, onDragEnd, component.id, selectedComponentIds, onGroupMove, onGroupDrag, onGroupDragEnd]);

    // Handle resize
    const handleResizeMouseDown = useCallback((e, direction) => {
        e.stopPropagation();
        e.preventDefault();

        setIsResizing(true);

        const startX = e.clientX;
        const startY = e.clientY;
        const startWidth = localDimensions.width;
        const startHeight = localDimensions.height;
        const startPosX = localPosition.x;
        const startPosY = localPosition.y;

        // Get zoom scale from TransformComponent for accurate resize
        let scale = 1;
        const transformComponent = wrapperRef.current?.closest('.react-transform-component');
        if (transformComponent) {
            const style = window.getComputedStyle(transformComponent);
            const transform = style.transform;
            if (transform && transform !== 'none') {
                const match = transform.match(/matrix\(([^,]+)/);
                if (match) {
                    scale = parseFloat(match[1]) || 1;
                }
            }
        }

        let finalPosition = { x: startPosX, y: startPosY };
        let finalDimensions = { width: startWidth, height: startHeight };

        const handleMouseMove = (moveEvent) => {
            moveEvent.preventDefault();
            // Divide delta by scale to compensate for zoom
            const deltaX = (moveEvent.clientX - startX) / scale;
            const deltaY = (moveEvent.clientY - startY) / scale;

            let newWidth = startWidth;
            let newHeight = startHeight;
            let newX = startPosX;
            let newY = startPosY;

            if (direction.includes('e')) {
                // Clamp width to not exceed artboard right edge
                newWidth = Math.max(40, Math.min(startWidth + deltaX, artboardWidth - startPosX));
            }
            if (direction.includes('w')) {
                const maxShrink = startWidth - 40;
                const clampedDelta = Math.max(-startPosX, Math.min(deltaX, maxShrink));
                newWidth = startWidth - clampedDelta;
                newX = startPosX + clampedDelta;
            }
            if (direction.includes('s')) {
                // Clamp height to not exceed artboard bottom edge
                newHeight = Math.max(20, Math.min(startHeight + deltaY, artboardHeight - startPosY));
            }
            if (direction.includes('n')) {
                const maxShrink = startHeight - 20;
                const clampedDelta = Math.max(-startPosY, Math.min(deltaY, maxShrink));
                newHeight = startHeight - clampedDelta;
                newY = startPosY + clampedDelta;
            }

            finalPosition = { x: newX, y: newY };
            finalDimensions = { width: newWidth, height: newHeight };

            // Update local state for smooth visual feedback
            setLocalPosition(finalPosition);
            setLocalDimensions(finalDimensions);
        };

        const handleMouseUp = () => {
            // Round final values
            const roundedPosition = {
                x: Math.round(finalPosition.x),
                y: Math.round(finalPosition.y)
            };
            const roundedDimensions = {
                width: Math.round(finalDimensions.width),
                height: Math.round(finalDimensions.height)
            };

            // Keep visual in sync
            setLocalPosition(roundedPosition);
            setLocalDimensions(roundedDimensions);
            setIsResizing(false);

            // Persist the final values
            const posChanged = roundedPosition.x !== startPosX || roundedPosition.y !== startPosY;
            const sizeChanged = roundedDimensions.width !== startWidth || roundedDimensions.height !== startHeight;

            if (sizeChanged) {
                pendingDimensions.current = roundedDimensions;
                onResize(roundedDimensions);
            }
            if (posChanged) {
                pendingPosition.current = roundedPosition;
                onMove(roundedPosition);
            }

            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [localPosition, localDimensions, onResize, onMove, artboardWidth, artboardHeight]);

    // Resize handle styles - modern, minimal Figma-like appearance
    const handleStyle = {
        position: 'absolute',
        background: '#ffffff',
        border: '1.5px solid #58a6ff',
        borderRadius: '50%',
        zIndex: 10,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
        transition: 'transform 0.1s ease, box-shadow 0.1s ease'
    };

    const edgeHandleStyle = {
        ...handleStyle,
        borderRadius: 3
    };

    const cornerSize = 6;
    const edgeThickness = 3;

    // Calculate visual position (apply group drag offset for selected non-dragging components)
    let visualX = localPosition.x;
    let visualY = localPosition.y;

    if (groupDragOffset &&
        !isDragging &&
        isSelected &&
        groupDragOffset.draggingId !== component.id) {
        visualX += groupDragOffset.deltaX;
        visualY += groupDragOffset.deltaY;
    }

    return (
        <div
            ref={wrapperRef}
            onClick={(e) => {
                e.stopPropagation();
                // Selection handled in handleMouseDown, just stop propagation here
            }}
            onMouseDown={handleMouseDown}
            style={{
                position: 'absolute',
                left: visualX,
                top: visualY,
                width: localDimensions.width,
                height: localDimensions.height,
                cursor: isDragging ? 'grabbing' : 'grab',
                opacity: isDragging ? 0.8 : 1,
                zIndex: component.z_index || 1,
                outline: isSelected ? '2px solid #58a6ff' : 'none',
                outlineOffset: 2,
                transition: (isDragging || isResizing) ? 'none' : 'outline 0.1s ease',
                userSelect: 'none',
                // Apply margin overrides
                marginTop: component.spacing_overrides?.margin?.top || 0,
                marginRight: component.spacing_overrides?.margin?.right || 0,
                marginBottom: component.spacing_overrides?.margin?.bottom || 0,
                marginLeft: component.spacing_overrides?.margin?.left || 0
            }}
        >
            {/* Component content */}
            <div style={{
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                // Apply padding overrides
                paddingTop: component.spacing_overrides?.padding?.top || 0,
                paddingRight: component.spacing_overrides?.padding?.right || 0,
                paddingBottom: component.spacing_overrides?.padding?.bottom || 0,
                paddingLeft: component.spacing_overrides?.padding?.left || 0
            }}>
                {children}
            </div>

            {/* Resize handles (only when selected) */}
            {isSelected && (
                <>
                    {/* Corner handles */}
                    <div
                        className="resize-handle"
                        onMouseDown={(e) => handleResizeMouseDown(e, 'nw')}
                        style={{
                            ...handleStyle,
                            top: -cornerSize / 2,
                            left: -cornerSize / 2,
                            width: cornerSize,
                            height: cornerSize,
                            cursor: 'nw-resize'
                        }}
                    />
                    <div
                        className="resize-handle"
                        onMouseDown={(e) => handleResizeMouseDown(e, 'ne')}
                        style={{
                            ...handleStyle,
                            top: -cornerSize / 2,
                            right: -cornerSize / 2,
                            width: cornerSize,
                            height: cornerSize,
                            cursor: 'ne-resize'
                        }}
                    />
                    <div
                        className="resize-handle"
                        onMouseDown={(e) => handleResizeMouseDown(e, 'sw')}
                        style={{
                            ...handleStyle,
                            bottom: -cornerSize / 2,
                            left: -cornerSize / 2,
                            width: cornerSize,
                            height: cornerSize,
                            cursor: 'sw-resize'
                        }}
                    />
                    <div
                        className="resize-handle"
                        onMouseDown={(e) => handleResizeMouseDown(e, 'se')}
                        style={{
                            ...handleStyle,
                            bottom: -cornerSize / 2,
                            right: -cornerSize / 2,
                            width: cornerSize,
                            height: cornerSize,
                            cursor: 'se-resize'
                        }}
                    />

                    {/* Edge handles */}
                    <div
                        className="resize-handle"
                        onMouseDown={(e) => handleResizeMouseDown(e, 'n')}
                        style={{
                            ...edgeHandleStyle,
                            top: -edgeThickness / 2,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 16,
                            height: edgeThickness,
                            cursor: 'n-resize'
                        }}
                    />
                    <div
                        className="resize-handle"
                        onMouseDown={(e) => handleResizeMouseDown(e, 's')}
                        style={{
                            ...edgeHandleStyle,
                            bottom: -edgeThickness / 2,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 16,
                            height: edgeThickness,
                            cursor: 's-resize'
                        }}
                    />
                    <div
                        className="resize-handle"
                        onMouseDown={(e) => handleResizeMouseDown(e, 'w')}
                        style={{
                            ...edgeHandleStyle,
                            left: -edgeThickness / 2,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: edgeThickness,
                            height: 16,
                            cursor: 'w-resize'
                        }}
                    />
                    <div
                        className="resize-handle"
                        onMouseDown={(e) => handleResizeMouseDown(e, 'e')}
                        style={{
                            ...edgeHandleStyle,
                            right: -edgeThickness / 2,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: edgeThickness,
                            height: 16,
                            cursor: 'e-resize'
                        }}
                    />
                </>
            )}
        </div>
    );
};

export default UIComponentWrapper;
